// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PageContent } from '@/components/page-content';
import {
  Sort,
  SortDirection,
  invert as invertDirection,
  sortDirectionSchema,
} from '@/constants/sort';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { PurchaseOrderListing } from '@/purchase-order/components/purchase-order-listing';
import { POPhase } from '@/purchase-order/constants/status';
import {
  PurchaseOrderSearchListing,
  PurchaseOrderSortProperty,
  purchaseOrderSearchListingSchema,
} from '@/purchase-order/hooks/use-purchase-orders';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

/**
 * Local storage key for persisting sort preferences.
 */
const LOCAL_STORAGE_SORT_KEY = 'purchase-order-sort';

/**
 * Default sort property when no preference is set.
 */
const DEFAULT_SORT_PROPERTY: PurchaseOrderSortProperty = 'poNumber';

/**
 * Default sort direction when no preference is set.
 */
const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

/**
 * Schema for validating sort preferences.
 */
const sortSchema = z.object({
  property: z.enum(['poNumber', 'status']),
  direction: sortDirectionSchema,
});

/**
 * Route configuration for purchase order listing page.
 */
export const Route = createFileRoute('/_app/purchase-orders/')({
  component: PurchaseOrderRoute,
  validateSearch: purchaseOrderSearchListingSchema,
});

/**
 * Main component for the purchase order listing page.
 */
function PurchaseOrderRoute() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  /**
   * Sort preference persistence in local storage.
   */
  const [sortPreference, setSortPreference] = useLocalStorage(
    LOCAL_STORAGE_SORT_KEY,
    sortSchema
  );

  /**
   * Current sort configuration.
   * Prioritizes URL parameters over local storage preferences.
   */
  const sort: Sort<PurchaseOrderSortProperty> = {
    property: search.sort ?? sortPreference?.property ?? DEFAULT_SORT_PROPERTY,
    direction:
      search.direction ?? sortPreference?.direction ?? DEFAULT_SORT_DIRECTION,
  };

  /**
   * Updates sort configuration.
   * Saves preference to local storage and updates URL.
   */
  const setSort = (
    property: PurchaseOrderSortProperty,
    direction: SortDirection
  ) => {
    setSortPreference({ property, direction });
    navigate({ search: (prev) => ({ ...prev, direction, sort: property }) });
  };

  /**
   * Handles column header click for sorting.
   * Toggles direction if same column, otherwise sorts ascending.
   */
  const handleSort = (property: PurchaseOrderSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invertDirection(sort.direction));
    }
    setSort(property, 'asc');
  };

  const searchParameters: PurchaseOrderSearchListing = {
    page: search.page,
    query: search.query,
    status: search.status,
    subStatuses: search.subStatuses,
    sort: search.sort,
    direction: search.direction,
    fromLocations: search.fromLocations,
    fromCustomerLocations: search.fromCustomerLocations,
    fromLocationCompanyTypes: search.fromLocationCompanyTypes,
    toLocations: search.toLocations,
    toCustomerLocations: search.toCustomerLocations,
    toLocationCompanyTypes: search.toLocationCompanyTypes,
  };

  const currentStatus: POPhase | 'all' = search.status ?? 'all';

  return (
    <PageContent>
      <PurchaseOrderListing
        currentStatus={currentStatus}
        searchParameters={searchParameters}
        sort={sort}
        onSort={handleSort}
        navigateFn={navigate}
      />
    </PageContent>
  );
}
